/**
 * Absolutely-positioned SVG overlay that draws EVERY horizontal arrow in
 * the FK Breadcrumb — both the same-row separators between consecutive
 * chips AND the down-branch arrow for each fork-parent — so they share
 * the same stroke width, the same chevron geometry, and the same tip
 * position relative to their target chip. The inline `<ChevronArrow />`
 * spans in `<FkBreadcrumbTreeRow />` are rendered `invisible` purely to
 * reserve the 12px slot they used to occupy.
 *
 * Each down-branch path is a smooth S-shape (rounded corner at the top AND
 * at the bottom):
 *
 *   source ─╮
 *           │
 *           │
 *           ╰──→ target
 *
 * Each same-row arrow is the horizontal "tail" of that S — exactly the
 * shape the down-branch makes after its bottom curve — so a chip's two
 * outgoing arrows (when it forks) read as the same arrow style on two
 * different rows.
 *
 * Geometry, measured from live DOM rects so it stays anchored to chips
 * across layout changes:
 *   - Paths start at `source.right + TRUNK_LEFT_GAP` at the source's
 *     centre Y. For down-branches this is followed by a quadratic curve
 *     down (`Q junctionX centerY junctionX centerY+R`), a vertical leg,
 *     and a mirror curve back to horizontal at the target's centre Y.
 *   - All paths end at `target.left - ARROW_GAP` with a chevron
 *     arrowhead whose legs are `ARROW_HEAD_LEN` long.
 *
 * For multiple siblings of the same fork-parent the vertical legs share
 * `junctionX` and stroke-overlap into a single visible trunk extending to
 * the deepest target.
 */

import { useLayoutEffect, useRef, useState } from 'react';
import type { ForkAnchor, TreeLayoutRow } from './breadcrumbTreeLayout';
import type { NavigationNodeId } from '../../../core/types/workspaceTypes';

type BranchArrowsOverlayProps = {
  /** Scrollable nav element — used to query chip elements. */
  readonly scrollContainerRef: React.RefObject<HTMLElement | null>;
  /** Inner content div that holds the rows; the SVG mounts here. */
  readonly contentContainerRef: React.RefObject<HTMLElement | null>;
  /** Rows in render order — used to draw same-row arrows. */
  readonly rows: ReadonlyArray<TreeLayoutRow>;
  /** One anchor per fork-parent — used to draw down-branch arrows. */
  readonly anchors: ReadonlyArray<ForkAnchor>;
};

type DrawnArrow = {
  readonly key: string;
  readonly d: string;
  readonly arrowD: string;
};

type OverlaySize = {
  readonly width: number;
  readonly height: number;
};

/** Gap from source chip's right edge to where the visible path starts. */
const TRUNK_LEFT_GAP = 4;
/** Rounded-corner radius for both the top and bottom curves. */
const CORNER_RADIUS = 4;
/** Gap between the arrowhead tip and the target chip's left edge. */
const ARROW_GAP = 4;
/** Length of each chevron arrowhead leg. */
const ARROW_HEAD_LEN = 3;
/** Stroke width — thin so the arrows feel quieter than the chip outlines. */
const STROKE_WIDTH = 1;

export function BranchArrowsOverlay({
  scrollContainerRef,
  contentContainerRef,
  rows,
  anchors,
}: BranchArrowsOverlayProps) {
  const [arrows, setArrows] = useState<ReadonlyArray<DrawnArrow>>([]);
  const [size, setSize] = useState<OverlaySize>({ width: 0, height: 0 });
  const rafRef = useRef<number | null>(null);

  useLayoutEffect(() => {
    const scrollEl = scrollContainerRef.current;
    const contentEl = contentContainerRef.current;
    if (!scrollEl || !contentEl) return;

    const measure = () => {
      const contentRect = contentEl.getBoundingClientRect();
      const drawn: DrawnArrow[] = [];

      // Same-row arrows — one per consecutive visible chip pair on every
      // row. Same chevron geometry as down-branches, so the two outgoing
      // arrows of a fork-parent read identically.
      for (const row of rows) {
        for (let i = 0; i + 1 < row.row.length; i += 1) {
          const sourceId = row.row[i];
          const targetId = row.row[i + 1];
          const sourceEl = queryChip(scrollEl, sourceId);
          const targetEl = queryChip(scrollEl, targetId);
          if (!sourceEl || !targetEl) continue;
          const s = sourceEl.getBoundingClientRect();
          const t = targetEl.getBoundingClientRect();
          const startX = s.right - contentRect.left + TRUNK_LEFT_GAP;
          const centerY = s.top + s.height / 2 - contentRect.top;
          const tipX = t.left - contentRect.left - ARROW_GAP;
          const stemEndX = tipX - ARROW_HEAD_LEN;
          // Defensive — if the slot collapses to nothing (e.g. zero-width
          // chevron placeholder), don't draw an inverted stem.
          if (stemEndX <= startX) continue;
          const d = `M ${startX} ${centerY} L ${stemEndX} ${centerY}`;
          const arrowD = chevronAt(tipX, centerY);
          drawn.push({
            key: `same-${sourceId}->${targetId}`,
            d,
            arrowD,
          });
        }
      }

      for (const anchor of anchors) {
        const sourceEl = queryChip(scrollEl, anchor.forkParentId);
        if (!sourceEl) continue;
        const s = sourceEl.getBoundingClientRect();
        const sRight = s.right - contentRect.left;
        const sCenterY = s.top + s.height / 2 - contentRect.top;
        const trunkStart = sRight + TRUNK_LEFT_GAP;
        const junctionX = trunkStart + CORNER_RADIUS;

        for (const targetId of anchor.downTargetIds) {
          const targetEl = queryChip(scrollEl, targetId);
          if (!targetEl) continue;
          const t = targetEl.getBoundingClientRect();
          const tLeft = t.left - contentRect.left;
          const tCenterY = t.top + t.height / 2 - contentRect.top;

          // Defensive — the layout puts off-spine rows BELOW their
          // fork-parent, but if anything ever inverts, skip rather than
          // draw a misleading upward arrow.
          if (tCenterY <= sCenterY) continue;

          const tipX = tLeft - ARROW_GAP;
          const stemEndX = tipX - ARROW_HEAD_LEN;
          // If the corner curve would overshoot the chevron, fall back to a
          // straight line from the curve end to the chevron stem.
          const bottomCurveEndX = junctionX + CORNER_RADIUS;
          const horizontalEndX = Math.max(bottomCurveEndX, stemEndX);

          const d = [
            // Start at the trunk's top, at the same Y as the source chip's
            // centre, so the path visually emerges from the chip area where
            // the inline same-row chevron also starts.
            `M ${trunkStart} ${sCenterY}`,
            // Quadratic curve from "going right" to "going down" — the
            // control point is the geometric corner (junctionX, sCenterY).
            `Q ${junctionX} ${sCenterY} ${junctionX} ${sCenterY + CORNER_RADIUS}`,
            // Vertical leg.
            `L ${junctionX} ${tCenterY - CORNER_RADIUS}`,
            // Bottom curve — "going down" → "going right".
            `Q ${junctionX} ${tCenterY} ${bottomCurveEndX} ${tCenterY}`,
            // Horizontal run to just before the arrowhead.
            `L ${horizontalEndX} ${tCenterY}`,
          ].join(' ');
          const arrowD = chevronAt(tipX, tCenterY);
          drawn.push({
            key: `${anchor.forkParentId}->${targetId}`,
            d,
            arrowD,
          });
        }
      }

      setArrows(drawn);
      setSize({
        width: Math.max(contentEl.scrollWidth, contentEl.clientWidth),
        height: Math.max(contentEl.scrollHeight, contentEl.clientHeight),
      });
    };

    const schedule = () => {
      if (rafRef.current !== null) return;
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null;
        measure();
      });
    };

    measure();

    let ro: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(schedule);
      ro.observe(scrollEl);
      ro.observe(contentEl);
    }
    return () => {
      ro?.disconnect();
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    };
  }, [anchors, rows, scrollContainerRef, contentContainerRef]);

  if (arrows.length === 0 || size.width === 0 || size.height === 0) {
    return null;
  }

  return (
    <svg
      width={size.width}
      height={size.height}
      viewBox={`0 0 ${size.width} ${size.height}`}
      aria-hidden="true"
      data-testid="fk-breadcrumb-branch-arrows"
      className="pointer-events-none absolute inset-0 text-muted-foreground/70"
      style={{ overflow: 'visible' }}
    >
      {arrows.map((a) => (
        <g key={a.key}>
          <path
            d={a.d}
            fill="none"
            stroke="currentColor"
            strokeWidth={STROKE_WIDTH}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d={a.arrowD}
            fill="none"
            stroke="currentColor"
            strokeWidth={STROKE_WIDTH}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </g>
      ))}
    </svg>
  );
}

function queryChip(root: HTMLElement, id: NavigationNodeId): HTMLElement | null {
  return root.querySelector<HTMLElement>(
    `[data-testid="fk-breadcrumb-chip-${cssEscape(id)}"]`,
  );
}

function chevronAt(tipX: number, tipY: number): string {
  return [
    `M ${tipX - ARROW_HEAD_LEN} ${tipY - ARROW_HEAD_LEN}`,
    `L ${tipX} ${tipY}`,
    `L ${tipX - ARROW_HEAD_LEN} ${tipY + ARROW_HEAD_LEN}`,
  ].join(' ');
}

/**
 * Minimal CSS.escape polyfill scope — chip ids in this app are slugs without
 * exotic characters, but we still escape defensively to avoid attribute
 * selector errors if a future id contains quotes / brackets.
 */
function cssEscape(value: string): string {
  if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
    return CSS.escape(value);
  }
  return value.replace(/(["\\\[\]])/g, '\\$1');
}
